/* 共通処理 */
const { HOMEPAGE } = require('./config/config');

/**
 * 表示用日時の作成
 * @param {string || Date} inputDate 日時
 * @param {string} format 成形フォーマット YYYY/MM/DD(d) hh:mm:ss
 * @param {array} dayList 曜日リスト（日, 月, ... 土の順番）
 * @return {string} 表示用の日時
 */
export const createDisplayDate = (
  inputDate,
  format = 'YYYY/MM/DD(d) hh:mm:ss',
  dayList = ['日', '月', '火', '水', '木', '金', '土']
) => {
  const jpDate = new Date(inputDate);

  // 無効な日時の場合
  if (jpDate.toString() === 'Invalid Date') {
    return '';
  }

  const year = jpDate.getFullYear();
  const month = `0${jpDate.getMonth() + 1}`.slice(-2);
  const date = `0${jpDate.getDate()}`.slice(-2);
  const day = dayList[jpDate.getDay()];
  const hours = `0${jpDate.getHours()}`.slice(-2);
  const minutes = `0${jpDate.getMinutes()}`.slice(-2);
  const seconds = `0${jpDate.getSeconds()}`.slice(-2);

  let resultDate = format;
  resultDate = resultDate.replace(/YYYY/g, year);
  resultDate = resultDate.replace(/MM/g, month);
  resultDate = resultDate.replace(/DD/g, date);
  resultDate = resultDate.replace(/d/g, day);
  resultDate = resultDate.replace(/hh/g, hours);
  resultDate = resultDate.replace(/mm/g, minutes);
  resultDate = resultDate.replace(/ss/g, seconds);

  return resultDate;
};

/**
 * 設定値マスタとの照合
 * @param {array} configList 設定値リスト
 * @param {string} targetKey 照合対象のkey名
 * @param {string} targetValue 照合対象のvalue
 * @param {string} wantedKey 設定値で取得したい情報のkey名
 * @return {string} 照合後の値
 */
export const collateConfigMasta = (
  configList = [],
  targetKey = '',
  targetValue = '',
  wantedKey = ''
) => {
  // valueが空文字の場合もあるため
  // if (configList.length === 0 || targetKey === '' || targetValue === '' || wantedKey === '') {
  if (configList.length === 0 || targetKey === '' || wantedKey === '') {
    return '';
  }

  const config = configList.find((element) => element[targetKey] === targetValue);

  const result = config[wantedKey] || '';

  return result;
};

/**
 * 文字の桁埋め
 * @param {string} str 桁埋めをする文字列
 * @param {number} digits 桁数
 * @param {string} char 桁埋めをする文字
 * @returns 桁埋めされた文字列
 */
export const createFillString = (str, digits, char) => {
  let fillStr = '';
  for (let i = 0; i < digits; i += 1) {
    fillStr += `${char}`;
  }
  fillStr += `${str}`;

  const result = fillStr.substr(-digits);

  return result;
};

/**
 * 属性の取得
 * @param {Object} attribute
 * @param {String} funcCd
 * @return {Object} タグの属性(hidden, readOnly, disabled)
 */
export const getAttribute = (attribute, funcCd) => attribute[funcCd] ?? {};

/**
 * 通話開始者表示
 * @param {int} tellStartPeople 通話開始者のフラグ
 * @returns 通話開始者の文言
 */
export const dispTellStartPeople = (tellStartPeople) => {
  let startTellWords = '';
  if (tellStartPeople === 1) {
    startTellWords = '※担当職員よりお電話いたします。';
  }
  if (tellStartPeople === 2) {
    startTellWords = '※予約者様は、上記の番号までお電話ください。';
  }
  return startTellWords;
};

/**
 * 判定対象のユーザー環境がモバイル端末（スマホ）かどうかを返す
 * - UA-CH (`navigator.userAgentData.mobile`) が利用可能な場合はそれを返す
 * - フォールバックとして UserAgent 文字列を判定する
 * @returns {boolean} `true` の場合はモバイル端末（スマホ）、それ以外は false
 */
const isMobile = () => {
  if (navigator?.userAgentData) {
    return navigator.userAgentData.mobile;
  }
  return /iPhone|Android.+Mobile/i.test(navigator.userAgent);
};

/**
 * 判定対象のユーザー環境がiPadかどうかを返す
 * iPadOSのSafariはUA文字列上Macと区別できないため、タッチ対応の有無も併せて判定する
 * @returns {boolean} `true` の場合はiPad
 */
const isIpad = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf('ipad') > -1 || (ua.indexOf('macintosh') > -1 && 'ontouchend' in document);
};

/**
 * 判定対象のユーザー環境がAndroidかどうかを返す
 * @returns {boolean} `true` の場合はAndroid
 */
const isAndroid = () => {
  if (navigator?.userAgentData) {
    return navigator.userAgentData.platform.toLowerCase() === 'android';
  }
  return !!window.navigator.userAgent.match(/Android/i);
};

/**
 * 判定対象のユーザー環境がタブレット（iPad または Androidタブレット）かどうかを返す
 * @returns {boolean} `true` の場合はタブレット
 */
const isProbTab = () => isIpad() || (!isMobile() && isAndroid());

/**
 * 判定対象のユーザー環境がPC（スマホ・タブレットではない端末）かどうかを返す
 * 画面幅ではなくデバイス自体（UA）で判定する
 * @returns {boolean} `true` の場合はPC
 */
export const isPC = () => !isMobile() && !isProbTab();

/**
 * 現在のページパスが除外対象かどうかを判定する。
 * @returns {boolean} 現在のパスが除外対象に一致する場合は `true`、それ以外では `false` を返す。
 */
export const isExcluded = () => {
  // ブラウザの現在の URL のパスを取得
  const path = window.location.pathname;

  // 除外対象のパターン。文字列と正規表現の両方がサポートされている。
  const excludePatterns = [
    new RegExp(`^${HOMEPAGE}/[0-9]{5}`), // Topページを除外
    new RegExp(`^${HOMEPAGE}/change/[0-9a-z]{32}`), // 予約変更・キャンセルTopページを除外
  ];

  // 現在の URL パスがいずれかの除外パターンに一致するかどうかを判定する。
  return excludePatterns.some((pattern) =>
    pattern instanceof RegExp ? pattern.test(path) : path.startsWith(pattern)
  );
};
